/**
 * Collapsible JSON tree + long string preview with copy.
 */
import React, { useMemo, useState } from 'react';
import { JSONTree } from 'react-json-tree';
import { Copy, ChevronDown, ChevronRight } from 'lucide-react';

const jsonTheme = {
  scheme: 'stepflow',
  base00: 'transparent',
  base01: '#e2e8f0',
  base02: '#cbd5e1',
  base03: '#94a3b8',
  base04: '#64748b',
  base05: '#334155',
  base06: '#1e293b',
  base07: '#0f172a',
  base08: '#dc2626',
  base09: '#ea580c',
  base0A: '#ca8a04',
  base0B: '#16a34a',
  base0C: '#0891b2',
  base0D: '#2563eb',
  base0E: '#7c3aed',
  base0F: '#c026d3'
};

function copyText(text) {
  void navigator.clipboard?.writeText(text);
}

export function LongStringPreview({ value, label = 'Text' }) {
  const str = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  const [open, setOpen] = useState(false);
  const long = str.length > 600;
  const shown = long && !open ? `${str.slice(0, 600)}…` : str;

  return (
    <div className="rounded-lg border border-gray-200 bg-slate-50/80 overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-2 py-1.5 bg-gray-100/80 border-b border-gray-200">
        <span className="text-xs font-medium text-gray-600">{label}</span>
        <button
          type="button"
          onClick={() => copyText(str)}
          className="inline-flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800"
        >
          <Copy className="w-3.5 h-3.5" />
          Copy
        </button>
      </div>
      <pre className="text-xs text-gray-800 p-3 whitespace-pre-wrap break-words max-h-[min(60vh,480px)] overflow-auto font-mono">
        {shown}
      </pre>
      {long && (
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="w-full py-1.5 text-xs text-blue-600 hover:bg-gray-100 border-t border-gray-200"
        >
          {open ? 'Show less' : 'Expand full text'}
        </button>
      )}
    </div>
  );
}

export default function CollapsibleDataBlock({ data, title = 'Payload', defaultCollapsed = true }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  const asObject = useMemo(() => {
    if (data == null) return { empty: true };
    if (typeof data === 'object' && !Array.isArray(data)) return data;
    return { value: data };
  }, [data]);

  const jsonReady = asObject && typeof asObject === 'object';

  return (
    <div className="rounded-lg border border-gray-200 bg-white overflow-hidden">
      <div className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm font-medium text-gray-800 bg-gray-50 border-b border-gray-100">
        <button
          type="button"
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-2 min-w-0 flex-1 hover:text-gray-950"
        >
          {collapsed ? <ChevronRight className="w-4 h-4 shrink-0" /> : <ChevronDown className="w-4 h-4 shrink-0" />}
          <span className="truncate">{title}</span>
        </button>
        <button
          type="button"
          onClick={() => copyText(JSON.stringify(asObject, null, 2))}
          className="inline-flex items-center gap-1 text-xs font-normal text-blue-600 shrink-0"
        >
          <Copy className="w-3.5 h-3.5" />
          JSON
        </button>
      </div>
      {!collapsed && jsonReady && (
        <div className="p-2 max-h-[min(70vh,560px)] overflow-auto text-xs">
          <JSONTree
            data={asObject}
            theme={jsonTheme}
            invertTheme={false}
            hideRoot={false}
            shouldExpandNodeInitially={(keyPath, data, level) => level < 2}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Renders `data` fields: long strings as LongStringPreview, nested objects as JSON tree blocks.
 */
export function SmartDataRenderer({ data, eventLabel }) {
  if (data == null || (typeof data === 'object' && Object.keys(data).length === 0)) {
    return <p className="text-xs text-gray-500 italic">No payload data</p>;
  }

  if (typeof data !== 'object' || Array.isArray(data)) {
    return <LongStringPreview value={data} label={eventLabel || 'Value'} />;
  }

  const entries = Object.entries(data);
  const blocks = [];

  for (const [k, v] of entries) {
    if (typeof v === 'string' && v.length > 400) {
      blocks.push(
        <div key={k} className="mb-3">
          <LongStringPreview value={v} label={k} />
        </div>
      );
    } else {
      blocks.push(
        <div key={k} className="mb-3">
          <CollapsibleDataBlock data={{ [k]: v }} title={k} defaultCollapsed />
        </div>
      );
    }
  }

  return <div className="space-y-2">{blocks}</div>;
}
